import { useState, createContext, useCallback, useMemo, useEffect } from "react";
import { PropTypes } from "prop-types";

import { convertFromUSD } from "@helpers/price";
import { isBrowser, createCookie, getCookie, getAllUrlParams } from "@helpers/browser";
import { alReportError } from "../helpers/ErrorBoundary/ALErrorBoundary";
import { useCurrentCountryCode } from "../hooks/usePrices";

import DISCOUNT_TYPES from "../constants/DiscountTypes";
import WHITELIST from "../constants/WhitelistedDiscount";
import useManyCheckInventory from "@hooks/useManyCheckInventory";

export const defaultDiscountContext = {
  discountInfo: null,
  localizedDiscount: [],
  setFinalDiscount: () => {},
};

export const DiscountContext = createContext(defaultDiscountContext);

function useLocalizedDiscount(countryCode, discount) {
  return useMemo(() => {
    if (!countryCode) {
      return { localizedDiscount: [] };
    }

    const result = {
      localizedDiscount: discount.edges
        .filter((d) =>
          d.node.countries.find(
            (c) => c.code === countryCode || (countryCode !== "US" && c.code === "RoW")
          )
        )
        .map(({ node }) => node),
    };
    return result;
  }, [countryCode, discount]);
}

function DiscountProvider({ discount, children }) {
  const [store, updateStore] = useState(defaultDiscountContext);
  const [priceMap, setPriceMap] = useState({});

  const countryCode = useCurrentCountryCode();
  const { localizedDiscount } = useLocalizedDiscount(countryCode, discount);

  const discountLocal = useMemo(() => {
    if (!isBrowser) {
      return "empty";
    }

    try {
      // Init discount local from cookies
      let initDiscountLocal = "empty";

      const uSource = getAllUrlParams()?.utm_source;
      if (uSource && WHITELIST.sourceAccepted.includes(uSource)) {
        // Extract discount info from url
        const uDiscountCode = getAllUrlParams()?.discount_code;
        let uDiscountAmount = getAllUrlParams()?.discount_a;
        const uDiscountType =
          getAllUrlParams()?.discount_t === "percentage"
            ? DISCOUNT_TYPES.SITEWIDE_PERCENT
            : getAllUrlParams()?.discount_t;

        if (uDiscountCode === "GET20") {
          uDiscountAmount = 25;
        }
        if (uDiscountCode && uDiscountType && uDiscountAmount) {
          // Store new discount to cookies
          const days = 1 / 24; // 1 / 24 days (1h) before Cookie expire
          createCookie("dSource", uSource, days);
          createCookie("dAmount", uDiscountAmount, days);
          createCookie("dCode", uDiscountCode, days);
          createCookie("dType", uDiscountType, days);

          // Set discount from urls params
          initDiscountLocal = {
            dSource: uSource,
            dAmount: uDiscountAmount,
            dCode: uDiscountCode,
            dType: uDiscountType,
            isFromParams: true,
            matchType: "exclude",
            matchTags: ["Final Sale", "EXCLUSION"],
          };
        }
      }

      return initDiscountLocal;
    } catch (e) {
      alReportError(e);
      return "empty";
    }
  }, []);

  let finalDiscounts = useMemo(() => {
    let finalDiscounts = [];

    if (localizedDiscount !== null) {
      finalDiscounts = localizedDiscount.map((discount) => {
        const finalDiscount = {
          isMaster: discount?.isMaster ?? false,
        };
        const dSource = getCookie("dSource");
        const dCode = getCookie("dCode");

        if (WHITELIST.codes.includes(dCode) || WHITELIST.sources.includes(dSource)) {
          finalDiscount.type = getCookie("dType");
          finalDiscount.value = parseInt(getCookie("dAmount"), 10);
          // finalDiscount.hasCrossedPrice = getCookie("dType") === DISCOUNT_TYPES.SITEWIDE_PERCENT;
          finalDiscount.code = getCookie("dCode");
          finalDiscount.isFromParams = true;
          finalDiscount.matchType = "exclude";
          finalDiscount.matchTags = ["Final Sale", "EXCLUSION"];
        }
        if (dCode === "GET20") {
          finalDiscount.value = 25;
        }

        if (
          discount &&
          (discountLocal == null || discountLocal === "empty" || discount?.isMaster === true)
        ) {
          finalDiscount.type = discount.type;
          finalDiscount.minimumPurchaseAmount = discount.minimumPurchaseAmount;
          finalDiscount.value = discount.percentageValue;
          finalDiscount.code = discount.discountCode;

          const compareNumbers = (a, b) => parseFloat(a) - parseFloat(b);
          if (finalDiscount.type === DISCOUNT_TYPES.QUANTITY_PERCENT) {
            discount.thresholdConditionQuantity.sort(compareNumbers);
            discount.thresholdResultPercentage.sort(compareNumbers);
            finalDiscount.discounts = discount.thresholdConditionQuantity.map((q, idx) => [
              parseInt(q, 10),
              parseFloat(discount.thresholdResultPercentage[idx]),
            ]);
            finalDiscount.thresholdConditionQuantity = discount.thresholdConditionQuantity;
            finalDiscount.thresholdResultPercentage = discount.thresholdResultPercentage;
          }
          if (discount.type === DISCOUNT_TYPES.AMOUNT_PERCENT) {
            discount.thresholdConditionAmount.sort(compareNumbers);
            discount.thresholdResultPercentage.sort(compareNumbers);
            finalDiscount.discounts = discount.thresholdConditionAmount.map((a, idx) => [
              parseFloat(a, 10),
              parseFloat(discount.thresholdResultPercentage[idx]),
            ]);
            finalDiscount.thresholdConditionAmount = discount.thresholdConditionAmount;
            finalDiscount.thresholdResultPercentage = discount.thresholdResultPercentage;
          }
          if (
            discount?.minimumPurchaseAmount &&
            discount?.giftProductHandles &&
            Array.isArray(discount.giftProductHandles)
          ) {
            finalDiscount.freegift = {
              minAmount: Number(discount.minimumPurchaseAmount),
              minEligibleQuantity:
                typeof discount.thresholdConditionQuantity?.[0] === "string"
                  ? Number(discount.thresholdConditionQuantity?.[0])
                  : null,
              products:
                discount.fields?.giftProductsDataRaw &&
                JSON.parse(discount.fields.giftProductsDataRaw),
              backupProduct:
                discount.giftProductHandleBackup && discount.giftProductHandleBackup !== "null"
                  ? discount.giftProductHandleBackup
                  : null,
            };
          } else {
            finalDiscount.freegift = null;
          }
          if (discount.productTagText && discount.productTagText !== "null") {
            finalDiscount.productSaleTag = {
              text: discount.productTagText,
              textColor: discount.productTagTextColor,
              backgroundColor: discount.productTagBackgroundColor,
            };
          }
          finalDiscount.matchType = discount?.matchType;
          finalDiscount.matchTags = discount?.matchTags || [];
          finalDiscount.globalProductText = discount?.globalProductText;
          finalDiscount.colorInformation = discount?.colorInformation;
          finalDiscount.isLuxe = discount?.isLuxe;
          finalDiscount.hasCrossedPrice = !!discount?.hasCrossedPrice; // false: Currently not existing in CMS
          finalDiscount.checkoutCalloutMinValue = discount.checkoutCalloutMinValue;
          finalDiscount.freeShippingThreshold = discount?.freeShippingThreshold;
        } else if (
          discountLocal != null &&
          discountLocal !== "empty" &&
          discount?.isMaster === false
        ) {
          finalDiscount.type = getCookie("dType");
          finalDiscount.value = parseInt(getCookie("dAmount"), 10);
          finalDiscount.hasCrossedPrice = getCookie("dType") === DISCOUNT_TYPES.SITEWIDE_PERCENT;
          finalDiscount.code = getCookie("dCode");
          finalDiscount.isFromParams = true;
          finalDiscount.exclusionTags = ["Final Sale", "EXCLUSION"];
        }
        return finalDiscount;
      });
    } else {
      finalDiscounts = ["empty"];
    }

    // Keep for a while, debug & report against discount usage
    if (isBrowser && window.exponea && finalDiscounts[0]?.code) {
      const debugDiscountData = {
        code: finalDiscounts[0]?.code,
        type: finalDiscounts[0]?.type,
        value: finalDiscounts[0]?.value,
        cookie_code: "",
        cookie_value: "",
        cookie_source: "",
        cookie_type: "",
      };
      if (discountLocal !== "empty") {
        debugDiscountData.cookie_code = discountLocal?.dCode;
        debugDiscountData.cookie_value = discountLocal?.dAmount;
        debugDiscountData.cookie_source = discountLocal?.dSource;
        debugDiscountData.cookie_type = discountLocal?.dType;
      }
      window.exponea.track("set_discount", debugDiscountData);
    }

    return finalDiscounts;
  }, [discountLocal, localizedDiscount]);

  useEffect(() => {
    if (countryCode === "US") {
      return;
    }
    const usdPricesToConvert = finalDiscounts.flatMap((disc) => {
      if (
        disc.type === DISCOUNT_TYPES.AMOUNT_PERCENT &&
        Array.isArray(disc.thresholdConditionAmount)
      ) {
        return disc.thresholdConditionAmount;
      }
      return [];
    });

    convertFromUSD(usdPricesToConvert, countryCode).then((conversionResults) => {
      setPriceMap(
        conversionResults.amounts.reduce((acc, curr, idx) => {
          acc[usdPricesToConvert[idx]] = curr;
          return acc;
        }, {})
      );
    });
  }, [finalDiscounts, countryCode]);

  // Apply converted discount to finalDiscounts
  finalDiscounts = useMemo(() => {
    if (countryCode === "US") {
      return finalDiscounts;
    }
    return finalDiscounts.map((disc) => {
      if (disc.type !== DISCOUNT_TYPES.AMOUNT_PERCENT) {
        return disc;
      }
      const convertedThresholdConditionAmount = disc.thresholdConditionAmount.map((amount) =>
        priceMap[amount] !== undefined ? priceMap[amount] : amount
      );
      return {
        ...disc,
        thresholdConditionAmount: convertedThresholdConditionAmount,
        discounts: convertedThresholdConditionAmount.map((a, idx) => [
          parseFloat(a),
          parseFloat(disc.thresholdResultPercentage[idx]),
        ]),
      };
    });
  }, [priceMap, finalDiscounts, countryCode]);

  const allGiftProducts = useMemo(() => {
    const products = [];
    finalDiscounts.forEach(({ freegift }) => {
      if (freegift && freegift.products?.length > 0) {
        products.push(...freegift.products);
      }
    });
    return products;
  }, [finalDiscounts]);

  const freeGiftProductsInventory = useManyCheckInventory(allGiftProducts);

  finalDiscounts = useMemo(
    () =>
      finalDiscounts.map((finalDiscount) => {
        if (finalDiscount.freegift && !finalDiscount.freegift.products) {
          return finalDiscount;
        }
        const soldOutProduct = finalDiscount.freegift?.products.find((p) => {
          const inventoryData = freeGiftProductsInventory.find((inv) => inv.handle === p.handle);
          return (
            !inventoryData?.availableForSale && p.handle !== finalDiscount.freegift.backupProduct
          );
        });

        /**
         * discount.freegift.products already includes all gift products (including the backup one)
         * We just need to filter out either the backup product or the sold-out item
         */
        const products =
          finalDiscount.freegift?.backupProduct && soldOutProduct
            ? finalDiscount.freegift?.products.filter((p) => p.handle !== soldOutProduct.handle)
            : finalDiscount.freegift?.products.filter(
                (p) => p.handle !== finalDiscount.freegift?.backupProduct
              );

        return {
          ...finalDiscount,
          freegift: {
            ...finalDiscount.freegift,
            products,
          },
        };
      }),
    [finalDiscounts, freeGiftProductsInventory]
  );

  const setFinalDiscount = useCallback(
    (update) => {
      updateStore((latestStore) => ({
        ...latestStore,
        finalDiscounts: [{ ...finalDiscounts[0], ...update }],
      }));
    },
    [finalDiscounts]
  );

  const providerData = useMemo(
    () => ({
      ...store,
      setFinalDiscount,
      localizedDiscount,
      discountLocal,
      discountInfo: store.finalDiscount || finalDiscounts,
    }),
    [store, setFinalDiscount, localizedDiscount, discountLocal, finalDiscounts]
  );

  return <DiscountContext.Provider value={providerData}>{children}</DiscountContext.Provider>;
}

DiscountProvider.propTypes = {
  discount: PropTypes.shape({
    edges: PropTypes.arrayOf(
      PropTypes.shape({
        node_locale: PropTypes.string,
        title: PropTypes.string,
        type: PropTypes.string,
        percentageValue: PropTypes.number,
        isMaster: PropTypes.bool,
        discountCode: PropTypes.string,
        minimumPurchaseAmount: PropTypes.number,
        countries: PropTypes.arrayOf(
          PropTypes.shape({
            code: PropTypes.string,
          })
        ),
        giftProductHandle: PropTypes.string,
        thresholdConditionQuantity: PropTypes.arrayOf(PropTypes.string),
        thresholdResultPercentage: PropTypes.arrayOf(PropTypes.string),
        matchType: PropTypes.oneOf(["include", "exclude"]),
        matchTags: PropTypes.arrayOf(PropTypes.string),
      })
    ).isRequired,
  }),
  children: PropTypes.node,
};

export default DiscountProvider;
